// A2F-650: formatting state model + reducer for the FRE redesign Step 2.
//
// State is a flat dotted-key map (see data/onboard-v2-fields.ts) and the
// "current" formatting is derived by reducing pills over BASE_FORMATTING.
// Pill removal is a filter + re-derive — no prior-value bookkeeping.
//
// Cross-component flow:
//
//   text box  --(translate-rule)-->  FormatRule.apply (nested)
//                                         |
//                                  flattenApply()
//                                         |
//                                         v
//                              pill.fieldChanges (flat)
//                                         |
//                  applyPills(base, pills): FormattingFields
//                                         |
//                                         v
//                            FieldCards / DocumentSample
//
// And on submit:
//
//   applyPills(base, pills) ─ formattersFromState ─> Formatters | undefined
//                                                    └─> buildProfile

import type {
  FormattingFields,
  FormattingValue,
} from '../data/onboard-v2-fields'
import { BASE_FORMATTING, FIELD_GROUPS } from '../data/onboard-v2-fields'
import type { Formatters, Rule, TableStylePreset } from '../types'
import type { TranslateSourceDetail } from './api'

// ── Custom-rule chip (Step 4) ────────────────────────────────────
// Mirrors the legacy components/onboard/CustomRuleInput.tsx CustomRuleChip
// shape so a future swap or merge is mechanical. Defined here to keep the
// FRE redesign components free of cross-version imports.

export interface CustomRuleChip {
  id: string
  label: string
  status: 'active' | 'coming_soon'
  rule?: Rule
  sourceDetail?: TranslateSourceDetail
}

export interface FormattingPill {
  id: string
  // User's raw input, truncated for display. Pattern length is recorded
  // separately for telemetry (non-PII proxy of the original prompt).
  label: string
  patternLength: number
  // Flat dotted-key overlay. Order matters in the reducer — later pills
  // win when keys collide.
  fieldChanges: FormattingFields
  // A2F-705: optional sibling channel for table-style preset patches.
  // The dotted-key reducer can't address an array slice, so when the
  // detector recognizes table styling ("emerald tables") it routes the
  // change here. ConfiguratorCore's add-pill handler applies this to
  // the *active* preset in `tableStyles[activeStyleIdx]`. Removal
  // semantics: removing a pill drops the field changes (filter +
  // re-derive) but does NOT undo tableStylePatch — applying to the
  // active preset is a destructive write, not an overlay. Keep
  // expectations clear in the UI copy if this becomes user-visible.
  // The pattern generalizes for A2F-700's other profile-array
  // surfaces — each gets a named slot here rather than a polymorphic
  // arrayPatch field.
  tableStylePatch?: Partial<TableStylePreset>
  // Always 'configurator' today — Step 2 is now the only source of
  // pills. The field stays for telemetry continuity (event prop) and
  // to keep the door open if a non-Step-2 source returns later.
  origin: 'configurator' | 'custom_rules'
  // Optional — only set when the pill came from a translate-rule
  // response (legacy code path; kept for type compat with telemetry).
  sourceDetail?: TranslateSourceDetail
}

export function applyPills(
  base: FormattingFields,
  pills: FormattingPill[],
): FormattingFields {
  let acc: FormattingFields = base
  for (const pill of pills) {
    acc = { ...acc, ...pill.fieldChanges }
  }
  return acc
}

/**
 * Compute the diff vs BASE_FORMATTING and return a Formatters object,
 * or undefined if no fields changed. Display-only fields (lists, etc.)
 * are filtered by walking FIELD_GROUPS and only including keys whose
 * card has persistable: true.
 *
 * D70 contract: the returned object only contains keys the user
 * explicitly customized. Unchanged fields stay absent so a future
 * CHRONA_DEFAULT bump still flows through to this user.
 */
export function formattersFromState(
  state: FormattingFields,
): Formatters | undefined {
  const persistableKeys = new Set<string>()
  for (const group of FIELD_GROUPS) {
    for (const card of group.cards) {
      if (!card.persistable) continue
      for (const k of card.keys) persistableKeys.add(k)
    }
  }

  const diff: FormattingFields = {}
  let touched = false
  for (const k of persistableKeys) {
    if (!(k in state)) continue
    if (state[k] !== BASE_FORMATTING[k]) {
      diff[k] = state[k]
      touched = true
    }
  }
  if (!touched) return undefined

  // Unflatten back to the Formatters shape. The dotted prefixes match
  // Formatters' actual keys (tables, headings, body, figureCaption).
  const out: Record<string, unknown> = {}
  for (const [path, value] of Object.entries(diff)) {
    setDeep(out, path.split('.'), value)
  }
  return out as Formatters
}

function setDeep(
  target: Record<string, unknown>,
  segments: string[],
  value: FormattingValue,
): void {
  let node: Record<string, unknown> = target
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i]
    if (typeof node[seg] !== 'object' || node[seg] === null) {
      node[seg] = {}
    }
    node = node[seg] as Record<string, unknown>
  }
  node[segments[segments.length - 1]] = value
}

/**
 * Inverse of formattersFromState: flatten a nested Formatters object
 * back into dotted-key FormattingFields. Used by ConfiguratorPage to
 * hydrate the field cards from a saved profile so an authed visit to
 * /configure shows the values the user previously saved.
 *
 * Only primitives (string | number | boolean) are emitted; nested
 * objects are walked, arrays are skipped (Formatters has no array
 * fields). Keys that don't appear in BASE_FORMATTING are still emitted
 * — the caller decides how to merge them with manualEdits.
 */
export function stateFromFormatters(formatters: Formatters): FormattingFields {
  const result: FormattingFields = {}
  flattenObject(formatters as Record<string, unknown>, '', result)
  return result
}

function flattenObject(
  obj: Record<string, unknown>,
  prefix: string,
  result: FormattingFields,
): void {
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      flattenObject(value as Record<string, unknown>, path, result)
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      result[path] = value
    }
  }
}

/** True if any of `keys` has a value in `state` differing from BASE_FORMATTING. */
export function isCardChanged(
  state: FormattingFields,
  keys: string[],
): boolean {
  for (const k of keys) {
    if (!(k in BASE_FORMATTING)) continue
    if (state[k] !== BASE_FORMATTING[k]) return true
  }
  return false
}

/**
 * A2F-701: deep-equal a TableStylePreset[] against the seed defaults to
 * decide whether to include `tableStyles` in the saved profile. D70's
 * inherit-don't-copy rule applies — an unedited array stays absent so
 * future server-side seed bumps still flow through to this user.
 *
 * JSON.stringify is the laziest deep-equal that works here: presets are
 * flat dicts of primitives + an id + name, never nested objects, never
 * Date / Map / Set, and the field key order is stable because both
 * inputs are constructed from the same code paths. Two-preset
 * defaults × ~14 fields each is well under 1KB serialized — cheap.
 */
export function tableStylesChanged(
  presets: TableStylePreset[],
  defaults: TableStylePreset[],
): boolean {
  if (presets.length !== defaults.length) return true
  return JSON.stringify(presets) !== JSON.stringify(defaults)
}
